import { EmployeeQueryParams, EmployeeDataArray, EmployeeModel } from '@app/interfaces/employee';
import { axiosApi } from './axios.api';
import { apiErrorMessage } from './ApiError';

const url = `/api/employees`;

export const getEmployeesApi = async (params?: EmployeeQueryParams): Promise<EmployeeDataArray | undefined> => {
  try {
    const response = await axiosApi.get<EmployeeDataArray>(url, { params });
    return response.data;
  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const getEmployeeApi = async (employeeId: number): Promise<EmployeeModel | undefined> => {
  try {
    const response = await axiosApi.get(`${url}/${employeeId}`);
    const data = response.data?.data;
    if (!data) return undefined;
    return { ...data.attributes, id: Number(data.id) } as EmployeeModel;
  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const createEmployeeApi = async (data: EmployeeModel) => {
  try {
    const response = await axiosApi.post(url, data);
    return response.data;
  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const updateEmployeeApi = async (employeeId: number, formData: EmployeeModel | FormData) => {
  try {
    const response = await axiosApi.put(`${url}/${employeeId}`, formData);
    return response.data;
  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const deleteEmployeeApi = async (employeeId: number) => {
  try {
    const response = await axiosApi.delete(`${url}/${employeeId}`);
    return response.data;
  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const exportEmployeeApi = async (params?: Record<string, unknown>) => {
  try {
    const response = await axiosApi.get(`${url}/export`, { params, responseType: 'blob' });

    const newUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = newUrl;
    link.setAttribute('download', `employees.xlsx`);
    document.body.appendChild(link);
    link.click();
  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export const getActivitiesApi = async (params: string) => {
  try {
    const response = await axiosApi.get(`${url}/activities`, { params });
    return response.data;
  } catch (error: any) {
    apiErrorMessage(error);
  }
};

export interface EmployeeLookupItem {
  id: number;
  employee_code: string;
  name: string;
  department: string;
  avatar_url?: string;
}

// Lightweight endpoint — any authenticated user, no salary or sensitive data.
interface EmployeeLookupParams {
  q?: string;
  /** Tên bộ phận cụ thể, vd "Giao hàng". */
  department?: string;
  /** Bí danh bộ phận cấu hình trong Settings — hiện hỗ trợ 'shipper'. */
  role?: 'shipper';
}

export const getEmployeeLookupApi = async (
  params?: string | EmployeeLookupParams,
): Promise<EmployeeLookupItem[]> => {
  try {
    const query = typeof params === 'string' ? { q: params } : params;
    const response = await axiosApi.get<{ rows: EmployeeLookupItem[] }>(`${url}/lookup`, {
      params: query && Object.keys(query).length > 0 ? query : undefined,
    });
    return response.data?.rows ?? [];
  } catch (error: any) {
    apiErrorMessage(error);
    return [];
  }
};

interface BonusHistoryExportParams {
  order_code?: string;
  from_date?: string;
  to_date?: string;
}

/** Xuất Excel tab "Lịch sử đơn hàng" của một nhân viên. */
export const exportEmployeeBonusHistoryApi = async (
  employeeId: number,
  params?: BonusHistoryExportParams,
) => {
  try {
    const response = await axiosApi.get(`${url}/${employeeId}/export_bonus_history`, {
      params,
      responseType: 'blob',
    });

    const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', 'employee_bonus_history.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (error: any) {
    apiErrorMessage(error);
    throw error;
  }
};
